import React, { Component } from 'react';
import { Document, Page} from 'react-pdf/dist/entry.noworker'
import 'react-pdf/src/Page/AnnotationLayer.css'
import { CircularProgress, Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/CloudDownloadOutlined';

function getFileKind(fileData) {
  const name = (fileData?.filename || '').toLowerCase();
  const type = (fileData?.contentType || '').toLowerCase();
  if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name)) return 'image';
  if (type.startsWith('text/') || /\.(txt|json|csv|log|md)$/.test(name)) return 'text';
  if (name.endsWith('.msg') || type === 'application/vnd.ms-outlook') return 'msg';
  return 'unsupported';
}

const isLikelyPdfBase64 = (b64) =>
  (b64 || '').replace(/^data:.*;base64,/, '').slice(0, 8).startsWith('JVBERi0');

export default class Viewer extends Component {
  state = { error: false, numPages: null };

  toDataUrl = (b64, ct) => (b64 ? `data:${ct || 'application/octet-stream'};base64,${b64}` : null);

  decodeText = (b64) => {
    try {
      const bin = atob(b64);
      const bytes = Uint8Array.from(bin, c => c.charCodeAt(0));
      return new TextDecoder('utf-8').decode(bytes);
    } catch { return '(unable to decode text)'; }
  };

  onLoadSuccess = ({ numPages }) => {
    if (this.state.numPages !== numPages) {
      this.setState({ numPages, error: false }, () => {
        this.props.numberOfPagesChange?.(numPages);
      });
    }
  };

  onLoadError = (err) => {
    console.error('PDF load error:', err);
    this.setState({ error: true });
  };

  renderNonPdf(kind, fileBase64, fileData) {
    const url = this.toDataUrl(fileBase64, fileData?.contentType);
    const name = fileData?.filename || 'download';

    if (kind === 'image' && url) {
      return <img src={url} alt={name} style={{ maxWidth: '100%', height: 'auto' }} />;
    }
    if (kind === 'text') {
      return (
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 700, overflow: 'auto',
                      padding: 8, border: '1px solid #e0e0e0', borderRadius: 4 }}>
          {this.decodeText(fileBase64)}
        </pre>
      );
    }
    return (
      <div style={{ fontSize: 14 }}>
        <div style={{ marginBottom: 8 }}>
          Preview not available for <b>{name}</b> ({fileData?.contentType || 'unknown type'}).
        </div>
        {url && (
          <Button variant="contained" color="primary" size="small" 
          sx={{
              width: 120,
              minWidth: 0,
              textTransform: 'none',
            }} 
            href={url} 
            download={name}
                  startIcon={<DownloadIcon fontSize="small"/>}>
            Download File
          </Button>
        )}
      </div>
    );
  }

  renderPdfOrFallback(fileBase64, fileData, pageNumber, scale, kind) {
    const isPdfKind = kind === 'pdf';
    const looksPdf = isLikelyPdfBase64(fileBase64);
    if (!isPdfKind) return this.renderNonPdf(kind, fileBase64, fileData);
    if (!looksPdf) {
      console.warn('First-load bytes are not a real PDF', {
        contentType: fileData?.contentType, filename: fileData?.filename,
        head: (fileBase64 || '').slice(0, 30)
      });
      return this.renderNonPdf('unsupported', fileBase64, fileData);
    }
    const pdfDataUrl = `data:application/pdf;base64,${fileBase64}`;
    return (
      <Document file={pdfDataUrl} onLoadSuccess={this.onLoadSuccess} onLoadError={this.onLoadError}>
        <Page pageNumber={pageNumber} scale={scale} renderTextLayer />
      </Document>
    );
  }

  render() {
    if (typeof DOMMatrix === 'undefined') {
      console.warn('DOMMatrix not available — likely non-browser environment.');
      return null;
    }
    const { fileBase64, fileData, pageNumber = 1, scale = 1 } = this.props;
    const { error } = this.state;

    if (!fileBase64 || !fileData) {
      return <CircularProgress style={{ width: '1.2rem', height: '1.2rem', borderWidth: '.1em' }} />;
    }

    const kind = error ? 'unsupported' : getFileKind(fileData);
    return this.renderPdfOrFallback(fileBase64, fileData, pageNumber, scale, kind);
  }
}